import React, { useEffect, useRef, useState } from 'react'

import { createPortal } from 'react-dom'

import Button from 'components/Form/Button'
import { generateAnalyticsReport } from 'utils/analytics-report'
import {
  Actions,
  Backdrop,
  CloseButton,
  Description,
  Dialog,
  ErrorMessage,
  Header,
  Option,
  OptionDescription,
  OptionTitle,
  Options,
  Title,
} from './styled'

export default function AnalyticsExportDialog({
  filters,
  filterOptions,
  language,
  loadSection,
  locale,
  onClose,
  open,
  section,
  sectionData,
  t,
}) {
  const [mode, setMode] = useState('current')
  const [generating, setGenerating] = useState(false)
  const [error, setError] = useState(false)

  const dialogRef = useRef(null)
  const closeRef = useRef(onClose)
  const generatingRef = useRef(generating)
  closeRef.current = onClose
  generatingRef.current = generating

  useEffect(() => {
    if (!open) return undefined
    const previousFocus = document.activeElement
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    setError(false)
    dialogRef.current?.focus()
    const handleKeyDown = event => {
      if (event.key === 'Escape' && !generatingRef.current) closeRef.current()
      if (event.key !== 'Tab') return
      const controls = [...dialogRef.current.querySelectorAll('button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), [tabindex="0"]')]
      const current = controls.indexOf(document.activeElement)
      if (!controls.length) {
        event.preventDefault()
        dialogRef.current.focus()
      } else if (event.shiftKey && current <= 0) {
        event.preventDefault()
        controls[controls.length - 1].focus()
      } else if (!event.shiftKey && (current === -1 || current === controls.length - 1)) {
        event.preventDefault()
        controls[0].focus()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', handleKeyDown)
      if (previousFocus?.isConnected) previousFocus.focus()
    }
  }, [open])

  const generate = async () => {
    setGenerating(true)
    setError(false)
    try {
      await generateAnalyticsReport({
        filters,
        filterOptions,
        language,
        loadSection,
        locale,
        mode,
        section,
        sectionData,
        t,
      })
      onClose()
    } catch (reportError) {
      console.error('[analytics-report]', reportError)
      setError(true)
    } finally {
      setGenerating(false)
    }
  }

  return !open ? null : createPortal(
    <Backdrop onMouseDown={event => event.target === event.currentTarget && !generating && onClose()}>
      <Dialog ref={dialogRef} tabIndex={-1} aria-busy={generating} aria-labelledby="analytics-export-title" aria-modal="true" role="dialog">
        <Header>
          <div>
            <Title id="analytics-export-title">{t('analytics_export_title')}</Title>
            <Description>{t('analytics_export_description')}</Description>
          </div>
          <CloseButton aria-label={t('analytics_export_cancel')} disabled={generating} onClick={onClose} type="button">×</CloseButton>
        </Header>

        <Options>
          <Option disabled={generating} aria-pressed={mode === 'current'} active={mode === 'current'} onClick={() => setMode('current')} type="button">
            <OptionTitle>{t('analytics_export_current_view')}</OptionTitle>
            <OptionDescription>{t('analytics_export_current_description')}</OptionDescription>
          </Option>
          <Option disabled={generating} aria-pressed={mode === 'full'} active={mode === 'full'} onClick={() => setMode('full')} type="button">
            <OptionTitle>{t('analytics_export_full_report')}</OptionTitle>
            <OptionDescription>{t('analytics_export_full_description')}</OptionDescription>
          </Option>
        </Options>

        {error ? <ErrorMessage role="alert">{t('analytics_export_error')}</ErrorMessage> : null}
        <Actions>
          <Button disabled={generating} link nospace onClick={onClose} width="auto">{t('analytics_export_cancel')}</Button>
          <Button color="blue" disabled={generating} loading={generating} nospace onClick={generate} width="auto">
            {generating ? t('analytics_export_generating') : t('analytics_export_generate')}
          </Button>
        </Actions>
      </Dialog>
    </Backdrop>,
    document.body,
  )
}
